import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createMtrSpec, fetchMtrSpecDetail, updateMtrSpec } from '#/api/imsMgmt/settings/mtrSpec';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import MtrSpecOptionsEditor from '#/modules/imsMgmt/settings/mtrSpec/components/MtrSpecOptionsEditor';
import {
  MTR_SPEC_TYPE_OPTIONS,
  mtrSpecFormSchema,
} from '#/modules/imsMgmt/settings/mtrSpec/schemas/mtrSpecSchema';
import {
  getMtrSpecApiErrorMessage,
  isMtrSpecVersionConflict,
} from '#/modules/imsMgmt/settings/mtrSpec/helper/apiError';
import {
  MTR_SPEC_FIELD_LABELS,
  resolveMtrSpecFieldLabel,
} from '#/modules/imsMgmt/settings/mtrSpec/helper/formValidationLabels';
import {
  mtrSpecDetailQueryKey,
  mtrSpecListQueryKeyPrefix,
} from '#/utils/queryKeys';

function resolveInitialSpecType(searchParams) {
  return searchParams.get('specType') === 'INPUT' ? 'INPUT' : 'SELECT';
}

function buildOptionsPayload(options) {
  return options.map((option) => {
    const item = {
      code: option.code,
      name: option.name,
    };

    if (option.id) {
      item.id = option.id;
    }

    return item;
  });
}

const CreateEditMtrSpecPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const updatedAtRef = useRef(null);
  const initialOptionsJsonRef = useRef(null);
  const initialSpecType = resolveInitialSpecType(searchParams);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: mtrSpecDetailQueryKey(id),
    queryFn: () => fetchMtrSpecDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createMtrSpec,
    updateFn: updateMtrSpec,
    buildPayload: (values) => {
      const payload = {
        code: values.code,
        name: values.name,
        remark: values.remark || null,
        specType: values.specType,
      };

      if (values.specType === 'SELECT') {
        const options = buildOptionsPayload(values.options);
        // 編輯時選項沒變就不送：整包替換會碰到已被引用的選項（be doc 54736105）
        if (!id || JSON.stringify(options) !== initialOptionsJsonRef.current) {
          payload.options = options;
        }
      } else if (!id) {
        payload.options = [];
      }

      if (id) {
        return {
          ...payload,
          updatedAt: updatedAtRef.current,
        };
      }

      return payload;
    },
    listQueryKeyPrefix: mtrSpecListQueryKeyPrefix,
    detailQueryKey: mtrSpecDetailQueryKey,
    getEditPath: ROUTES.mtrSpecEdit,
    getErrorMessage: (error) => getMtrSpecApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isMtrSpecVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(mtrSpecFormSchema),
    defaultValues: {
      code: '',
      name: '',
      remark: '',
      specType: initialSpecType,
      options: initialSpecType === 'SELECT' ? [{ code: '', name: '' }] : [],
    },
  });

  const specType = useWatch({ control, name: 'specType' });
  const isSelectType = specType === 'SELECT';

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    initialOptionsJsonRef.current = detail.specType === 'SELECT' && Array.isArray(detail.options)
      ? JSON.stringify(buildOptionsPayload(detail.options))
      : null;
    resetValidationSummary();
    reset({
      code: detail.code ?? '',
      name: detail.name ?? '',
      remark: detail.remark ?? '',
      specType: detail.specType ?? 'SELECT',
      options: Array.isArray(detail.options) && detail.options.length > 0
        ? detail.options.map((option) => ({
          id: option.id,
          code: option.code ?? '',
          name: option.name ?? '',
        }))
        : [{ code: '', name: '' }],
    });
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯規格' : '新增規格';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const specTypeLabel = specType === 'INPUT' ? '輸入項目' : '固定項目';

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.mtrSpec}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MTR_SPEC_FIELD_LABELS}
                fieldLabelResolver={resolveMtrSpecFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="relative w-full pb-5">
                    <div className="form-section__header">
                      <h2 className="form-block__title mb-0! mr-2">基本資料</h2>
                    </div>
                    <div className="relative mt-5 space-y-4">
                      {isEdit ? (
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                        類型：
                          {specTypeLabel}
                        （建立後不可變更）
                        </div>
                      ) : (
                        <FloatingSelect
                          id="specType"
                          label="類型"
                          options={MTR_SPEC_TYPE_OPTIONS}
                          control={control}
                          errors={errors}
                        />
                      )}

                      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                        <FloatingInput
                          id="code"
                          label="代號"
                          register={register}
                          errors={errors}
                        />
                        <FloatingInput
                          id="name"
                          label="名稱"
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  {isSelectType ? (
                    <section className="relative w-full pb-5">
                      <MtrSpecOptionsEditor
                        control={control}
                        register={register}
                        errors={errors}
                      />
                    </section>
                  ) : null}
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtrSpec}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMtrSpecPage;
